import { useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router';
import type { Permission } from '@zentera/shared';
import { Spinner } from '@zentera/ui';
import { api, ApiError, onAuthError } from './api';
import type { Me } from './types';

export const meQueryKey = ['me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => api.get<Me>('/api/auth/me'),
    retry: false,
    staleTime: 60_000,
  });
}

export function useCan() {
  const { data } = useMe();
  return (p: Permission) => data?.permissions.includes(p) ?? false;
}

/** Redireciona para a etapa de autenticação pendente: login, MFA ou troca de senha. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, error, isPending } = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(
    () =>
      onAuthError(() => {
        void qc.invalidateQueries({ queryKey: meQueryKey });
      }),
    [qc, navigate],
  );

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center text-fg-3">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (error) {
    if (error instanceof ApiError && error.status === 401) {
      const next = location.pathname + location.search;
      return <Navigate to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }
  if (data.mfaState === 'verify') return <Navigate to="/login/mfa" replace />;
  if (data.mfaState === 'setup') return <Navigate to="/setup/mfa" replace />;
  if (data.mustChangePassword) return <Navigate to="/setup/password" replace />;
  return <>{children}</>;
}

/** Destino seguro pós-login: apenas caminhos internos. */
export function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
}
